import { useState, useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Paper,
  Divider,
} from '@mui/material'
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab'
import {
  History as HistoryIcon,
  CheckCircle as ActiveIcon,
  SwapHoriz as ChangeIcon,
  Person as TeacherIcon,
  MenuBook as SubjectIcon,
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { getHistoryDirect } from '@timetable/api'
import type { TimetableRecordWithRelations, TimetableDayOfWeek } from '@timetable/types'
import { format, parseISO } from 'date-fns'
import { queryKeys } from '../../lib/queryKeys'

interface TimetableHistoryViewerProps {
  classSectionId: string
  academicYear: string
}

interface PeriodSlot {
  dayOfWeek: TimetableDayOfWeek
  periodId: string
  periodLabel: string
}

export function TimetableHistoryViewer({
  classSectionId,
  academicYear,
}: TimetableHistoryViewerProps) {
  const [selectedSlot, setSelectedSlot] = useState<PeriodSlot | null>(null)

  // Fetch all active records to build the slot selector
  const { data: activeRecords, isLoading: isLoadingActive } = useQuery({
    queryKey: queryKeys.timetable.list({ class_section_id: classSectionId, academic_year: academicYear, active_only: true }),
    queryFn: async () => {
      const { getTimetableRecordsDirect } = await import('@timetable/api')
      return getTimetableRecordsDirect({
        class_section_id: classSectionId,
        academic_year: academicYear,
        active_only: true,
      })
    },
    enabled: !!classSectionId && !!academicYear,
  })

  // Build list of unique period slots
  const periodSlots = useMemo(() => {
    if (!activeRecords) return []

    const slots: PeriodSlot[] = []
    const seen = new Set<string>()

    activeRecords.forEach((record) => {
      const key = `${record.day_of_week}-${record.period_id}`
      if (!seen.has(key)) {
        seen.add(key)
        slots.push({
          dayOfWeek: record.day_of_week as TimetableDayOfWeek,
          periodId: record.period_id,
          periodLabel: `${record.day_of_week} - Period ${record.period?.period_number || ''}`,
        })
      }
    })

    // Sort by day and period
    const dayOrder: Record<string, number> = {
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
      Sunday: 7,
    }

    slots.sort((a, b) => {
      const dayDiff = dayOrder[a.dayOfWeek] - dayOrder[b.dayOfWeek]
      if (dayDiff !== 0) return dayDiff
      return a.periodId.localeCompare(b.periodId)
    })

    return slots
  }, [activeRecords])

  // Fetch history for selected slot
  const {
    data: historyRecords,
    isLoading: isLoadingHistory,
    error: historyError,
  } = useQuery({
    queryKey: queryKeys.timetable.list({
      class_section_id: classSectionId,
      day_of_week: selectedSlot?.dayOfWeek,
      period_id: selectedSlot?.periodId,
      academic_year: academicYear,
      history: true,
    }),
    queryFn: () =>
      getHistoryDirect(
        classSectionId,
        selectedSlot!.dayOfWeek,
        selectedSlot!.periodId,
        academicYear
      ),
    enabled: !!selectedSlot && !!classSectionId && !!academicYear,
  })

  // Find the current active record (valid_to IS NULL)
  const activeRecordId = useMemo(() => {
    if (!historyRecords) return null
    const active = historyRecords.find((r) => r.valid_to === null)
    return active?.id || null
  }, [historyRecords])

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Present'
    try {
      return format(parseISO(dateStr), 'MMM dd, yyyy')
    } catch {
      return dateStr
    }
  }

  const isActive = (record: TimetableRecordWithRelations) => {
    return record.id === activeRecordId
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, overflow: 'auto' }}>
        <Stack spacing={3}>
          {/* Header */}
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryIcon color="primary" />
            <Typography variant="h6">Timetable History</Typography>
          </Box>

          {/* Period Slot Selector */}
          <FormControl fullWidth>
            <InputLabel id="period-slot-label">Select Period Slot</InputLabel>
            <Select
              labelId="period-slot-label"
              id="period-slot-select"
              value={selectedSlot ? `${selectedSlot.dayOfWeek}-${selectedSlot.periodId}` : ''}
              label="Select Period Slot"
              onChange={(e) => {
                const value = e.target.value
                if (!value) {
                  setSelectedSlot(null)
                  return
                }
                const slot = periodSlots.find(
                  (s) => `${s.dayOfWeek}-${s.periodId}` === value
                )
                setSelectedSlot(slot || null)
              }}
              disabled={isLoadingActive}
            >
              <MenuItem value="">
                <em>Select a period slot</em>
              </MenuItem>
              {periodSlots.map((slot) => (
                <MenuItem
                  key={`${slot.dayOfWeek}-${slot.periodId}`}
                  value={`${slot.dayOfWeek}-${slot.periodId}`}
                >
                  {slot.periodLabel}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Loading State */}
          {isLoadingActive && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {/* Empty State */}
          {!selectedSlot && !isLoadingActive && (
            <Alert severity="info">
              Select a period slot to view its change history
            </Alert>
          )}

          {/* History Timeline */}
          {selectedSlot && (
            <>
              {isLoadingHistory && (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              )}

              {historyError && (
                <Alert severity="error">
                  Failed to load history: {(historyError as Error).message}
                </Alert>
              )}

              {historyRecords && historyRecords.length === 0 && (
                <Alert severity="info">No history found for this period slot</Alert>
              )}

              {historyRecords && historyRecords.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Showing {historyRecords.length} record
                    {historyRecords.length !== 1 ? 's' : ''}
                  </Typography>

                  <Timeline position="right">
                    {historyRecords.map((record, index) => {
                      const isCurrentActive = isActive(record)
                      const isLast = index === historyRecords.length - 1

                      return (
                        <TimelineItem key={record.id}>
                          <TimelineOppositeContent
                            sx={{ flex: 0.3, py: 2 }}
                            color="text.secondary"
                          >
                            <Typography variant="body2" fontWeight="medium">
                              {formatDate(record.valid_from)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              to
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {formatDate(record.valid_to)}
                            </Typography>
                          </TimelineOppositeContent>

                          <TimelineSeparator>
                            <TimelineDot
                              color={isCurrentActive ? 'success' : 'grey'}
                              variant={isCurrentActive ? 'filled' : 'outlined'}
                            >
                              {isCurrentActive ? <ActiveIcon /> : <ChangeIcon />}
                            </TimelineDot>
                            {!isLast && <TimelineConnector />}
                          </TimelineSeparator>

                          <TimelineContent sx={{ py: 2 }}>
                            <Paper
                              elevation={isCurrentActive ? 3 : 1}
                              sx={{
                                p: 2,
                                bgcolor: isCurrentActive
                                  ? 'success.50'
                                  : 'background.paper',
                                border: isCurrentActive
                                  ? '2px solid'
                                  : '1px solid',
                                borderColor: isCurrentActive
                                  ? 'success.main'
                                  : 'divider',
                              }}
                            >
                              <Stack spacing={1.5}>
                                {/* Active Badge */}
                                {isCurrentActive && (
                                  <Chip
                                    label="Currently Active"
                                    color="success"
                                    size="small"
                                    icon={<ActiveIcon />}
                                    sx={{ alignSelf: 'flex-start' }}
                                  />
                                )}

                                {/* Teacher Info */}
                                <Box display="flex" alignItems="center" gap={1}>
                                  <TeacherIcon fontSize="small" color="action" />
                                  <Typography variant="body2" color="text.secondary">
                                    Teacher:
                                  </Typography>
                                  <Typography variant="body1" fontWeight="medium">
                                    {record.teacher?.name || 'Unknown'}
                                  </Typography>
                                </Box>

                                {/* Subject Info */}
                                <Box display="flex" alignItems="center" gap={1}>
                                  <SubjectIcon fontSize="small" color="action" />
                                  <Typography variant="body2" color="text.secondary">
                                    Subject:
                                  </Typography>
                                  <Typography variant="body1" fontWeight="medium">
                                    {record.subject?.subject_name || 'Unknown'}
                                  </Typography>
                                </Box>

                                <Divider />

                                {/* Superseded By Info */}
                                {record.superseded_by && (
                                  <Alert severity="info" sx={{ py: 0.5 }}>
                                    <Typography variant="caption">
                                      Superseded by record: {record.superseded_by}
                                    </Typography>
                                  </Alert>
                                )}

                                {/* Metadata */}
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Record ID: {record.id}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Paper>
                          </TimelineContent>
                        </TimelineItem>
                      )
                    })}
                  </Timeline>
                </Box>
              )}
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}
